import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import Button from './Button';
import css from './SubscriptionHero.css';

const SubscriptionHero = ({
  listing,
  provider,
  subscription,
  intl,
  isProvider,
  onPickup,
  onComplete,
  isPickingUp,
  isCompleting,
  isCancelling,
  onCancel,
  canPickup,
  canComplete,
  canCancel,
}) => {
  const imageUrl = listing?.images?.[0]?.attributes?.variants?.['square-small']?.url;
  const monthlyPrice = subscription?.attributes?.monthlyPrice;

  // Odczyt z processState oraz dodatkowych flag
  const processState = subscription?.attributes?.processState;
  const unpaidSince = subscription?.attributes?.unpaidSince;

  // Stany logiki biznesowej
  const isExpired = processState === 'payment-expired' || processState === 'declined';
  const isPaymentPending = processState === 'payment-pending';
  const isActive = processState === 'active';
  const hasPaymentIssue = isActive && !!unpaidSince;

  const listingId = listing?.id?.uuid;
  const subscriptionId = subscription?.id?.uuid;

  // Wyznaczenie właściwej klasy CSS dla badge statusu
  const getStatusClass = () => {
    if (isExpired) return css['status-payment-expired'];
    if (hasPaymentIssue) return css['status-active-unpaid'];
    return css[`status-${processState?.toLowerCase()}`] || '';
  };

  // Klucz i18n dla badge statusu
  const statusTranslationKey = hasPaymentIssue
    ? 'SubscriptionHero.status.active-unpaid'
    : `SubscriptionHero.status.${processState}`;

  return (
    <header className={css.heroSection}>
      <div
        className={classNames(css.heroCard, {
          [css.heroCardUnpaid]: hasPaymentIssue,
          [css.heroCardExpired]: isExpired,
        })}
      >
        {/* 1. ALERT: Problem z płatnością cykliczną (ACTIVE + unpaidSince) */}
        {hasPaymentIssue && (
          <div className={css.unpaidAlert}>
            <div className={css.alertIcon}>⚠️</div>
            <div className={css.alertContent}>
              <h3 className={css.alertTitle}>
                <FormattedMessage
                  id={isProvider ? 'SubscriptionHero.paymentIssueProviderTitle' : 'SubscriptionHero.paymentIssueCustomerTitle'}
                  defaultValue={isProvider ? 'PROBLEM Z PŁATNOŚCIĄ KLIENTA' : 'PROBLEM Z PŁATNOŚCIĄ'}
                />
              </h3>
              <p className={css.alertDescription}>
                {isProvider ? (
                  <FormattedMessage
                    id="SubscriptionHero.paymentIssueProviderDesc"
                    defaultValue="Nie udało się pobrać kolejnej miesięcznej raty od klienta. Skontaktuj się z nim, aby zaktualizował kartę."
                  />
                ) : (
                  <FormattedMessage
                    id="SubscriptionHero.paymentIssueCustomerDesc"
                    defaultValue="Nie udało nam się pobrać miesięcznej opłaty. Zaktualizuj swoją kartę, aby uniknąć przerwania subskrypcji."
                  />
                )}
              </p>
            </div>
          </div>
        )}

        {/* 2. ALERT: Oczekuje na dokończenie płatności inicjalnej (payment-pending) */}
        {isPaymentPending && (
          <div className={css.pendingAlert}>
            <div className={css.alertIcon}>💳</div>
            <div className={css.alertContent}>
              <h3 className={css.alertTitle}>
                <FormattedMessage
                  id={isProvider ? 'SubscriptionHero.pendingProviderTitle' : 'SubscriptionHero.pendingCustomerTitle'}
                  defaultValue={isProvider ? 'OCZEKUJE NA PŁATNOŚĆ KLIENTA' : 'PŁATNOŚĆ W TOKU'}
                />
              </h3>
              <p className={css.alertDescription}>
                {isProvider ? (
                  <FormattedMessage
                    id="SubscriptionHero.pendingProviderDesc"
                    defaultValue="Klient rozpoczął rezerwację, ale nie sfinalizował jeszcze opłaty inicjalnej."
                  />
                ) : (
                  <FormattedMessage
                    id="SubscriptionHero.pendingCustomerDesc"
                    defaultValue="Dokończ płatność inicjalną, aby zamówienie zostało przekazane do realizacji."
                  />
                )}
              </p>
            </div>
            {!isProvider && subscriptionId && (
              <Link to={`/subpayment/${subscriptionId}`} className={css.payNowBtn}>
                <FormattedMessage id="SubscriptionHero.btnPayNow" defaultValue="Opłać teraz" />
              </Link>
            )}
          </div>
        )}

        {/* 3. ALERT: Płatność wygasła / Transakcja odrzucona (payment-expired / declined) */}
        {isExpired && (
          <div className={css.expiredAlert}>
            <div className={css.alertIcon}>⏱️</div>
            <div className={css.alertContent}>
              <h3 className={css.alertTitle}>
                <FormattedMessage
                  id={isProvider ? 'SubscriptionHero.expiredProviderTitle' : 'SubscriptionHero.expiredCustomerTitle'}
                  defaultValue={isProvider ? 'REZERWACJA WYGASŁA' : 'CZAS NA PŁATNOŚĆ MINĄŁ'}
                />
              </h3>
              <p className={css.alertDescription}>
                {isProvider ? (
                  <FormattedMessage
                    id="SubscriptionHero.expiredProviderDesc"
                    defaultValue="Klient nie opłacił rezerwacji w wyznaczonym czasie. Zamówienie zostało automatycznie wygaszone."
                  />
                ) : (
                  <FormattedMessage
                    id="SubscriptionHero.expiredCustomerDesc"
                    defaultValue="Czas na dokonanie płatności minął. Jeśli nadal chcesz wypożyczyć ten sprzęt, złóż nowe zamówienie."
                  />
                )}
              </p>
            </div>
            {!isProvider && listingId && (
              <Link to={`/subcheckout/${listingId}`} className={css.reorderLink}>
                <FormattedMessage id="SubscriptionHero.btnReorder" defaultValue="Złóż nowe zamówienie" />
              </Link>
            )}
          </div>
        )}

        <div className={css.heroMain}>
          <div className={css.imageWrapper}>
            {imageUrl ? (
              <img
                src={imageUrl}
                className={css.listingImage}
                alt={listing?.attributes?.title}
              />
            ) : (
              <div className={css.imagePlaceholder}>
                <FormattedMessage id="SubscriptionHero.noPhoto" defaultValue="Brak zdjęcia" />
              </div>
            )}
          </div>

          <div className={css.heroContent}>
            <div className={css.titleWrapper}>
              <span className={classNames(css.statusBadge, getStatusClass())}>
                <FormattedMessage
                  id={statusTranslationKey}
                  defaultValue={processState}
                />
              </span>
              <h1 className={css.productTitle}>{listing?.attributes?.title}</h1>
              <p className={css.providerInfo}>
                <FormattedMessage
                  id="SubscriptionHero.providerPartner"
                  values={{
                    name: (
                      <span className={css.providerName}>
                        {provider?.attributes?.name}
                      </span>
                    ),
                  }}
                />
              </p>
            </div>

            <div className={css.priceBox}>
              <div className={css.priceValue}>
                {formatMoney(intl, monthlyPrice)}
              </div>
              <div className={css.priceLabel}>
                <FormattedMessage id="SubscriptionHero.perMonth" defaultValue="miesięcznie" />
              </div>
            </div>
          </div>
        </div>

        {/* Akcje sklepowe (Provider) */}
        {isProvider && !isExpired && (canPickup || canComplete || canCancel) && (
          <div className={css.shopActions}>
            <div className={css.shopLabel}>
              <FormattedMessage id="SubscriptionHero.shopManagement" defaultValue="Zarządzanie zamówieniem" />
            </div>
            <div className={css.shopButtons}>
              {canPickup && (
                <Button
                  variant="primary"
                  onClick={onPickup}
                  isLoading={isPickingUp}
                >
                  <FormattedMessage id="SubscriptionHero.btnPickup" defaultValue="Wydaj sprzęt" />
                </Button>
              )}

              {canComplete && (
                <Button
                  variant="secondary"
                  onClick={onComplete}
                  isLoading={isCompleting}
                >
                  <FormattedMessage id="SubscriptionHero.btnComplete" defaultValue="Zakończ i rozlicz" />
                </Button>
              )}

              {canCancel && (
                <Button
                  variant="danger"
                  onClick={onCancel}
                  isLoading={isCancelling}
                >
                  <FormattedMessage id="SubscriptionHero.btnCancel" defaultValue="Anuluj subskrypcję" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default injectIntl(SubscriptionHero);